import {
  Breadcrumb,
  getTheme,
  IBreadcrumbItem,
  Icon,
  mergeStyleSets,
  Pivot,
  PivotItem,
  PrimaryButton,
  Stack,
  Text,
} from '@fluentui/react';
import * as React from 'react';
import { navigateTo } from '../../../src/hooks';
import { Question } from '../../Question';
import { MsCertifiedBadge } from '../../MsCertifiedBadge';

const PRODUCT_URL = 'https://reenhanced.com/product/power-automate-for-wpforms/';
const LOGO_URL = 'https://reenhanced.com/wp-content/uploads/2023/09/combined-logo-1.png';
const SCREENSHOT_SETTINGS = 'https://reenhanced.com/wp-content/uploads/2023/09/settings.jpg';
const SCREENSHOT_TRIGGER = 'https://reenhanced.com/wp-content/uploads/2023/09/configure-trigger.jpg';
const SCREENSHOT_FLOW = 'https://reenhanced.com/wp-content/uploads/2023/09/example-flow-with-dynamic-content.jpg';
const SCREENSHOT_ENTRIES = 'https://reenhanced.com/wp-content/uploads/2023/09/get-entries-options.jpg';

const theme = getTheme();

const style = mergeStyleSets({
  container: {},
  heroBanner: {
    background: `linear-gradient(135deg, ${theme.palette.themePrimary} 0%, ${theme.palette.themeDarker} 100%)`,
    borderRadius: 12, padding: '32px 36px', marginBottom: 28, color: '#fff',
  },
  heroLogoRow: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 },
  heroLogo: { height: 52, borderRadius: 8, objectFit: 'contain' as const, flexShrink: 0, backgroundColor: '#fff', padding: '4px 8px' },
  heroTitle: { ...theme.fonts.xxLarge, color: '#fff', margin: 0, fontWeight: 700 },
  heroSubtitle: { ...theme.fonts.mediumPlus, color: 'rgba(255,255,255,0.88)', marginBottom: 20, lineHeight: '1.6' },
  heroPricing: { ...theme.fonts.small, color: 'rgba(255,255,255,0.7)', marginTop: 8, display: 'block' as const },
  trustRow: { display: 'flex', flexWrap: 'wrap' as const, gap: 10, marginBottom: 28 },
  trustPill: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    backgroundColor: theme.palette.neutralLighterAlt,
    border: `1px solid ${theme.palette.neutralLight}`,
    borderRadius: 20, padding: '5px 12px', fontSize: 13,
    color: theme.palette.neutralPrimary,
  },
  sectionTitle: { ...theme.fonts.xLarge, fontWeight: 700, marginBottom: 16, marginTop: 24 },
  featureGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14, marginBottom: 24 },
  featureCard: {
    backgroundColor: theme.palette.neutralLighterAlt,
    border: `1px solid ${theme.palette.neutralLight}`,
    borderRadius: 8, padding: '14px 16px',
  },
  featureIcon: { color: theme.palette.themePrimary, fontSize: 20, marginBottom: 6, display: 'block' as const },
  featureTitle: { ...theme.fonts.medium, fontWeight: 700, marginBottom: 4 },
  featureText: { ...theme.fonts.small, color: theme.palette.neutralSecondary, lineHeight: '1.5' },
  screenshot: {
    width: '100%', maxWidth: 560, borderRadius: 8,
    border: `1px solid ${theme.palette.neutralLight}`,
    display: 'block' as const, margin: '16px 0',
  },
  useCaseList: { listStyle: 'none', paddingLeft: 0, margin: 0 },
  useCaseItem: { display: 'flex', gap: 12, padding: '12px 0', borderBottom: `1px solid ${theme.palette.neutralLighter}` },
  useCaseIcon: { color: theme.palette.themePrimary, fontSize: 20, flexShrink: 0, marginTop: 2 },
  useCaseTitle: { ...theme.fonts.medium, fontWeight: 700, marginBottom: 2 },
  useCaseDesc: { ...theme.fonts.small, color: theme.palette.neutralSecondary },
  guaranteeBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    backgroundColor: '#e6f4ea', border: '1px solid #a8d5b0',
    borderRadius: 8, padding: '8px 16px', marginTop: 12,
  },
  stepList: { listStyle: 'none', paddingLeft: 0 },
  stepItem: {
    display: 'flex', gap: 16, padding: '10px 0',
    borderBottom: `1px solid ${theme.palette.neutralLighter}`,
  },
  stepNum: {
    flexShrink: 0, width: 28, height: 28, borderRadius: '50%',
    backgroundColor: theme.palette.themePrimary, color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, fontWeight: 700,
  },
  privacyBox: {
    backgroundColor: theme.palette.neutralLighterAlt,
    border: `1px solid ${theme.palette.neutralLight}`,
    borderRadius: 6, padding: '14px 20px', marginTop: 16,
  },
  highlight: { backgroundColor: 'rgba(255, 245, 100, 0.65)', padding: '0 4px', borderRadius: 2 },
});

const useCases = [
  { icon: 'ExcelLogo', title: 'Live Excel reports from form entries', desc: 'Every WPForms submission is appended to a shared Excel workbook in real time — no manual exports or CSV downloads.' },
  { icon: 'SharepointLogo', title: 'Log entries to SharePoint', desc: 'Build a SharePoint list or document library that populates automatically as your forms are submitted.' },
  { icon: 'TeamsLogo', title: 'Teams notifications on new entries', desc: 'Announce new form submissions in a Microsoft Teams channel the moment they arrive.' },
  { icon: 'ContactCard', title: 'CRM sync from form submissions', desc: 'Push WPForms data straight into Dynamics 365 or Salesforce, with automated follow-up campaigns triggered immediately.' },
  { icon: 'Send', title: 'Automated marketing campaigns', desc: 'Add submitters to Dynamics 365 Customer Insights journeys or email-marketing sequences the instant they submit.' },
  { icon: 'Workflow', title: 'Complex internal workflows', desc: 'Use form submissions to kick off multi-step approval workflows, notifications, and document-management processes.' },
  { icon: 'Database', title: 'Access any historical entry', desc: 'Read any previous WPForms entry directly from a Power Automate flow — enabling lookups, audits, and re-processing.' },
];

export const WPForms: React.FC = () => {
  const breadcrumbs: IBreadcrumbItem[] = [
    { text: 'Products', key: 'products', onClick: (ev) => { ev.preventDefault(); navigateTo('/products'); } },
    { text: 'Power Automate for WPForms', key: 'wpforms' },
  ];

  return (
    <>
      <Breadcrumb items={breadcrumbs} />
      <div className={style.container}>

        {/* ── Hero ── */}
        <div className={style.heroBanner}>
          <div className={style.heroLogoRow}>
            <img src={LOGO_URL} alt="Power Automate for WPForms" className={style.heroLogo} />
            <h1 className={style.heroTitle}>Power Automate for WPForms</h1>
          </div>
          <p className={style.heroSubtitle}>
            The <strong style={{ color: '#fff' }}>Microsoft Certified</strong> Power Automate connector
            for WPForms. Send form submissions to over <strong style={{ color: '#fff' }}>1,000+ services</strong> —
            Dynamics 365, Teams, Excel, SharePoint, Outlook, and more — or read any previous entry, all
            without writing a single line of code.
          </p>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 14 }}>
            <PrimaryButton
              text="Get Started — First Month Free"
              iconProps={{ iconName: 'ShoppingCart' }}
              onClick={() => { window.location.href = PRODUCT_URL; }}
              styles={{ root: { backgroundColor: '#fff', color: theme.palette.themePrimary, borderColor: '#fff' } }}
            />
            <MsCertifiedBadge style={{ filter: 'brightness(2)' }} />
          </Stack>
          <span className={style.heroPricing}>
            From $29 / month &nbsp;&bull;&nbsp; Free trial with code <strong style={{ color: '#fff' }}>onemonthfree</strong> &nbsp;&bull;&nbsp; 30-day money-back guarantee
          </span>
        </div>

        {/* ── Trust signals ── */}
        <div className={style.trustRow}>
          {[
            { icon: 'Certificate', text: 'Microsoft Certified' },
            { icon: 'Code', text: 'No Code Required' },
            { icon: 'Shield', text: '30-Day Money-Back Guarantee' },
            { icon: 'Lock', text: 'Your Data Goes Directly to Power Automate' },
            { icon: 'Database', text: 'Read Any Previous Entry' },
            { icon: 'PlugConnected', text: '1,000+ Connected Services' },
          ].map(t => (
            <span key={t.icon} className={style.trustPill}>
              <Icon iconName={t.icon} style={{ color: theme.palette.themePrimary }} />
              {t.text}
            </span>
          ))}
        </div>

        {/* ── Guarantee badge ── */}
        <div className={style.guaranteeBadge}>
          <Icon iconName="Shield" style={{ color: '#2e7d32', fontSize: 20 }} />
          <div>
            <Text block style={{ fontWeight: 700, color: '#1b5e20' }}>30-Day No-Questions-Asked Money-Back Guarantee</Text>
            <Text block style={{ fontSize: 12, color: '#2e7d32' }}>Unhappy for any reason? Contact us for a full refund.</Text>
          </div>
        </div>

        <Pivot>
          {/* ── Overview ── */}
          <PivotItem headerText="Overview">
            <Text block style={{ ...theme.fonts.mediumPlus as React.CSSProperties, lineHeight: '1.7', marginTop: 16, marginBottom: 20 }}>
              Power Automate for WPForms is the <span className={style.highlight}>quickest and easiest way to connect WPForms to Power Automate</span>.
              This plugin maps form submissions to dynamic fields available instantly in any flow, and also lets you
              access data from <strong>any previous form entry</strong> directly from Power Automate —
              enabling lookups, audits, and re-processing with zero manual exports.
            </Text>

            <img src={SCREENSHOT_TRIGGER} alt="Configure WPForms trigger in Power Automate" className={style.screenshot} />

            <div className={style.sectionTitle}>Why Power Automate for WPForms?</div>
            <div className={style.featureGrid}>
              {[
                { icon: 'LightningBolt', title: 'Instant Trigger on Submission', text: 'Every WPForms submission fires a Power Automate trigger with all field values mapped and ready to use.' },
                { icon: 'Database', title: 'Access Historical Entries', text: 'Read or query any previous entry from Power Automate — not just react to new submissions.' },
                { icon: 'Lock', title: 'Privacy by Default', text: 'Entry data is sent directly from your WordPress site to Power Automate. It never passes through our servers.' },
                { icon: 'Key', title: 'Unique Secret Key', text: 'Each site generates a unique secret key on setup, securing your Power Automate connection independently of your license.' },
                { icon: 'Unlimited', title: 'Unlimited Flows', text: 'Run as many flows per form as needed. No per-submission fees.' },
                { icon: 'PlugConnected', title: 'Works with All WPForms Add-ons', text: 'Fully compatible with the WPForms ecosystem — payments, user registration, surveys, and more.' },
              ].map(f => (
                <div key={f.icon} className={style.featureCard}>
                  <Icon iconName={f.icon} className={style.featureIcon} />
                  <div className={style.featureTitle}>{f.title}</div>
                  <div className={style.featureText}>{f.text}</div>
                </div>
              ))}
            </div>

            <img src={SCREENSHOT_FLOW} alt="Dynamic content from WPForms in a Power Automate flow" className={style.screenshot} />
            <img src={SCREENSHOT_ENTRIES} alt="Get entries from WPForms in Power Automate" className={style.screenshot} />
          </PivotItem>

          {/* ── Use Cases ── */}
          <PivotItem headerText="Use Cases">
            <div className={style.sectionTitle} style={{ marginTop: 16 }}>What You Can Automate — No Code Required</div>
            <ul className={style.useCaseList}>
              {useCases.map((uc, i) => (
                <li key={i} className={style.useCaseItem}>
                  <Icon iconName={uc.icon} className={style.useCaseIcon} />
                  <div>
                    <div className={style.useCaseTitle}>{uc.title}</div>
                    <div className={style.useCaseDesc}>{uc.desc}</div>
                  </div>
                </li>
              ))}
            </ul>
          </PivotItem>

          {/* ── Setup ── */}
          <PivotItem headerText="Setup">
            <div className={style.sectionTitle} style={{ marginTop: 16 }}>Up and Running in Minutes</div>
            <img src={SCREENSHOT_SETTINGS} alt="WPForms Power Automate settings page" className={style.screenshot} />
            <ol className={style.stepList}>
              {[
                'Purchase and download the plugin as a .zip file',
                'Upload to WordPress via the built-in plugin installer',
                'Activate the plugin',
                'Go to WPForms → Settings → Integrations → Power Automate',
                'Enter your license key and save the page',
                'Your site generates a unique Secret Key to secure the connection',
                'Create a connection in Power Automate and access all your forms and entries',
              ].map((step, i) => (
                <li key={i} className={style.stepItem}>
                  <span className={style.stepNum}>{i + 1}</span>
                  <span style={{ paddingTop: 4 }}>{step}</span>
                </li>
              ))}
            </ol>

            <div className={style.privacyBox}>
              <Text variant="mediumPlus" block style={{ fontWeight: 'bold', marginBottom: 6 }}>
                <Icon iconName="PrivacyLogo" /> Privacy First
              </Text>
              <Text block>
                Your form entry data is sent directly from your WordPress site to Power Automate — it never
                passes through our servers. We have no visibility into your submissions and cannot assist
                with troubleshooting specific entry data, but our support team is always available for
                configuration and connectivity questions.
              </Text>
            </div>
          </PivotItem>

          {/* ── FAQ ── */}
          <PivotItem headerText="FAQ">
            <Question question="Do I need a Power Automate license?">
              <p>Flows run under the account that creates them, so the flow creator needs a Power Automate license (often already included in Microsoft 365 business plans). Each flow processes unlimited submissions, but each form needs its own dedicated flow.</p>
            </Question>
            <Question question="Is there a free trial?">
              <p>Yes — use coupon code <code>onemonthfree</code> at checkout on the 1-month, 1-domain plan for a completely free trial month. Upgrade any time from My Account.</p>
            </Question>
            <Question question="How does the plugin work technically?">
              <p>When a WPForms entry is submitted, your site sends the field data directly to Power Automate where the connector parses and maps the fields. No data passes through our servers at any point.</p>
            </Question>
            <Question question="What happens if I cancel my license?">
              <p>Your form data will stop being sent to Power Automate until the license is renewed. All historical data already in your flows remains unaffected.</p>
            </Question>
            <Question question="Does this work with WPForms add-ons?">
              <p>Yes — fully compatible with the WPForms add-on ecosystem including payments, user registration, surveys, and more.</p>
            </Question>
            <Question question="What if I'm unhappy with the product?">
              <p>We offer a 30-day no-questions-asked money-back guarantee. Contact us for a full refund.</p>
            </Question>
          </PivotItem>
        </Pivot>

        {/* ── Bottom CTA ── */}
        <div style={{
          backgroundColor: theme.palette.neutralLighterAlt,
          border: `1px solid ${theme.palette.neutralLight}`,
          borderRadius: 10, padding: '24px 28px', marginTop: 24,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap' as const, gap: 16,
        }}>
          <div>
            <Text block style={{ ...theme.fonts.large as React.CSSProperties, fontWeight: 700, marginBottom: 4 }}>
              Ready to automate WPForms?
            </Text>
            <Text block style={{ color: theme.palette.neutralSecondary }}>
              From $29 / month &nbsp;&bull;&nbsp; Free trial with code <strong>onemonthfree</strong> &nbsp;&bull;&nbsp; 30-day money-back guarantee
            </Text>
          </div>
          <PrimaryButton
            text="Get Started — First Month Free"
            iconProps={{ iconName: 'ShoppingCart' }}
            onClick={() => { window.location.href = PRODUCT_URL; }}
          />
        </div>

      </div>
    </>
  );
};

export default WPForms;
